import { useReducer } from 'react';

const initialState = {
    selectedStrokes: new Set(),
    selectionBox: null,
    isSelecting: false,
    isDragging: false,
    isResizing: false,
    isRotating: false,
    startPoint: null,
    dragStartPoint: null,
    resizeHandle: null,
    bounds: null,
    cursor: 'default',
    originalStrokePoints: new Map(),
    originalStrokeData: new Map(),
    transformedStrokes: [],
    rotationAngle: 0,
    initialRotationAngle: 0
};

const selectionReducer = (state, action) => {
    switch (action.type) {
        case 'SELECT_STROKES':
            return { ...state, selectedStrokes: new Set(action.payload) };
        case 'ADD_TO_SELECTION':
            const newSelection = new Set(state.selectedStrokes);
            newSelection.add(action.payload);
            return { ...state, selectedStrokes: newSelection };
        case 'REMOVE_FROM_SELECTION':
            const updatedSelection = new Set(state.selectedStrokes);
            updatedSelection.delete(action.payload);
            return { ...state, selectedStrokes: updatedSelection };
        case 'CLEAR_SELECTION':
            return { 
                ...state, 
                selectedStrokes: new Set(),
                selectionBox: null,
                bounds: null,
                transformedStrokes: [],
                originalStrokePoints: new Map(),
                originalStrokeData: new Map()
            };
        case 'SET_SELECTION_BOX':
            return { ...state, selectionBox: action.payload };
        case 'START_SELECTING':
            return { ...state, isSelecting: true, startPoint: action.payload };
        case 'STOP_SELECTING':
            return { ...state, isSelecting: false, startPoint: null };
        case 'START_DRAGGING':
            return { ...state, isDragging: true, dragStartPoint: action.payload };
        case 'STOP_DRAGGING':
            return { ...state, isDragging: false, dragStartPoint: null };
        case 'START_RESIZING':
            return { ...state, isResizing: true, resizeHandle: action.payload };
        case 'STOP_RESIZING':
            return { ...state, isResizing: false, resizeHandle: null };
        case 'START_ROTATING':
            return { 
                ...state, 
                isRotating: true,
                initialRotationAngle: action.payload 
            };
        case 'STOP_ROTATING':
            return { ...state, isRotating: false, rotationAngle: 0 };
        case 'SET_BOUNDS':
            return { ...state, bounds: action.payload };
        case 'SET_CURSOR':
            return { ...state, cursor: action.payload };
        case 'SET_ORIGINAL_POINTS':
            return { ...state, originalStrokePoints: action.payload };
        case 'SET_ORIGINAL_DATA':
            return { ...state, originalStrokeData: action.payload };
        case 'SET_TRANSFORMED_STROKES':
            return { ...state, transformedStrokes: action.payload };
        case 'UPDATE_ROTATION':
            return { ...state, rotationAngle: action.payload };
        case 'RESET':
            return initialState;
        default:
            return state;
    }
};

export const useSelection = () => {
    const [state, dispatch] = useReducer(selectionReducer, initialState);

    const actions = {
        selectStrokes: (strokes) => dispatch({ type: 'SELECT_STROKES', payload: strokes }),
        addToSelection: (strokeId) => dispatch({ type: 'ADD_TO_SELECTION', payload: strokeId }),
        removeFromSelection: (strokeId) => dispatch({ type: 'REMOVE_FROM_SELECTION', payload: strokeId }),
        clearSelection: () => dispatch({ type: 'CLEAR_SELECTION' }),
        setSelectionBox: (box) => dispatch({ type: 'SET_SELECTION_BOX', payload: box }),
        startSelecting: (point) => dispatch({ type: 'START_SELECTING', payload: point }),
        stopSelecting: () => dispatch({ type: 'STOP_SELECTING' }),
        startDragging: (point) => dispatch({ type: 'START_DRAGGING', payload: point }),
        stopDragging: () => dispatch({ type: 'STOP_DRAGGING' }),
        startResizing: (handle) => dispatch({ type: 'START_RESIZING', payload: handle }),
        stopResizing: () => dispatch({ type: 'STOP_RESIZING' }),
        startRotating: (angle) => dispatch({ type: 'START_ROTATING', payload: angle }),
        stopRotating: () => dispatch({ type: 'STOP_ROTATING' }),
        setBounds: (bounds) => dispatch({ type: 'SET_BOUNDS', payload: bounds }),
        setCursor: (cursor) => dispatch({ type: 'SET_CURSOR', payload: cursor }),
        setOriginalPoints: (points) => dispatch({ type: 'SET_ORIGINAL_POINTS', payload: points }),
        setOriginalData: (data) => dispatch({ type: 'SET_ORIGINAL_DATA', payload: data }),
        setTransformedStrokes: (strokes) => dispatch({ type: 'SET_TRANSFORMED_STROKES', payload: strokes }),
        updateRotation: (angle) => dispatch({ type: 'UPDATE_ROTATION', payload: angle }),
        reset: () => dispatch({ type: 'RESET' })
    };

    return [state, actions];
};
